import { Button, Card, CardContent, CardMedia, Tooltip } from "@mui/material";
import config from "../config";
import { ImageInfo } from "../models/ResponseModels/SearchResponse";
import StarIcon from "@mui/icons-material/Star";
import CropOriginalIcon from "@mui/icons-material/CropOriginal";
import ShareIcon from "@mui/icons-material/Share";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { PostShareModel } from "../models/PostShareModel";
import ErrorsString from "../ErrorsString";
import saveAs from "file-saver";
import { logDownload } from "../Utilities";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeSharpIcon from "@mui/icons-material/AccessTime";

interface ImageCardProps {
  item: ImageInfo;
  index: number;
  baseUrl: string;
  setSelectedUuid: React.Dispatch<React.SetStateAction<string>>;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShareId: React.Dispatch<React.SetStateAction<number>>;
  setShowAlertShare: React.Dispatch<React.SetStateAction<boolean>>;
  setAlertShareMessage: React.Dispatch<React.SetStateAction<string>>;
  setOpenDialogShareLink: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCopied: React.Dispatch<React.SetStateAction<boolean>>;
  setImageUrlShare: React.Dispatch<React.SetStateAction<string>>;
}

export default function ImageCard({
  item,
  index,
  baseUrl,
  setSelectedUuid,
  setOpenModal,
  setShareId,
  setShowAlertShare,
  setAlertShareMessage,
  setOpenDialogShareLink,
  setIsCopied,
  setImageUrlShare,
}: ImageCardProps) {
  function openImageDetailsModal(uuid: string) {
    // Apri modale
    setSelectedUuid(uuid);
    setOpenModal(true);
  }

  function shareDialogLink(
    event: React.MouseEvent<HTMLButtonElement>,
    imageUuid: string,
    cardIndex: number,
  ) {
    console.log("Starting API call PostShareModel...");
    (async () => {
      const response = await PostShareModel(imageUuid);
      //console.log("PostShareModel response: " + JSON.stringify(response));

      const responseData = response?.data;
      const statusCode = response?.status;
      setShareId(cardIndex);

      if (responseData === undefined) {
        setShowAlertShare(true);
        setAlertShareMessage("Error in share url composition");
        setOpenDialogShareLink(true);
        return;
      }

      if (statusCode !== 200) {
        setShowAlertShare(true);
        setAlertShareMessage(ErrorsString.errorMessageGeneric);
        setOpenDialogShareLink(true);
        return;
      } else {
        setShowAlertShare(false);
        setAlertShareMessage("");
        setIsCopied(false);
        const encryptedString = responseData.encryptedString;
        const urlToShare = baseUrl + "/public/landing/" + encryptedString;

        //console.log("urlToShare: "+urlToShare);

        //show shared url
        setImageUrlShare(urlToShare);
        setOpenDialogShareLink(true);
      }
    })();
  }

  function _getImageFileName(image) {
    let imageFileName = "";

    if (image.title_image) {
      imageFileName = image.title_image;
    } else {
      const today = new Date();
      const formattedDate = today.toLocaleDateString();
      imageFileName = "image" + formattedDate;
    }

    const fileExtension = image.fileExtension ? image.fileExtension : "jpg";
    imageFileName = imageFileName + "." + fileExtension;

    return imageFileName;
  }

  function downloadOriginalImage(image: ImageInfo) {
    if (image.essence) {
      const url =
        config.cloudfrontImgUrl + "" + image.essence + config.showImagesQuery;

      let imageFileName = _getImageFileName(image);

      saveAs(url, imageFileName);

      logDownload(image.uuid, null);
    }
  }

  return (
    <Card
      className="mx-auto"
      sx={{ width: 310, height: 310 }}
      key={"img_" + index}
      onClick={() => openImageDetailsModal(item.uuid)}
    >
      <CardMedia
        component="div"
        sx={{
          height: 140,
          backgroundImage: `url(${
            item.proxy != null
              ? config.cloudfrontImgUrl +
                "" +
                item.proxy +
                config.showImagesQuery
              : config.placeholderImageUrl
          })`,
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
        key={"img_card_media_" + index}
      />

      <CardContent key={"img_card_content_" + index}>
        <div className="flex flex-column justify-between min-h-8">
          <div
            className={`mt-auto mb-auto ${
              item.isGreenStar && item.isGreenStar === true
                ? "visible"
                : "invisible"
            } `}
          >
            <StarIcon style={{ color: "green" }} />
          </div>

          <div
            className={`mt-auto mb-auto ${
              item.isReleased === false ? "visible" : "invisible"
            } `}
          >
            <Tooltip title="This image is released" placement="top-start">
              <CalendarMonthIcon color="primary" />
            </Tooltip>
          </div>

          <div
            className={`mt-auto mb-auto ${
              item.isEmbargoFlag === true ? "visible" : "invisible"
            } `}
          >
            <Tooltip title="Embargo is pending" placement="top-start">
              <AccessTimeSharpIcon color="primary" />
            </Tooltip>
          </div>

          <div
            className={`mt-auto mb-auto ${
              item.hasCrop && item.hasCrop === true ? "visible" : "invisible"
            } `}
          >
            <Tooltip
              title="This image has crops available"
              placement="top-start"
            >
              <CropOriginalIcon color="primary" />
            </Tooltip>
          </div>
        </div>
        <div className="text-base min-h-10 font-skyMedium">
          {item.title_image}
        </div>

        <div
          className={`text-base text-sky-400 font-skyMedium text-center bg-sky-500 text-white rounded-[10px] ${
            item.isInternalOnly && item.isInternalOnly === true
              ? "visible"
              : "invisible"
          }`}
        >
          Internal Only
        </div>

        <div className="flex justify-end justify-between mt-3">
          {item.isShareable && (
            <Button
              variant="contained"
              color="warning"
              type="submit"
              onClick={(e) => {
                e.stopPropagation();
                shareDialogLink(e, item.uuid, index);
              }}
              style={{ width: "47%" }}
            >
              <ShareIcon className="mr-2" />
              Share
            </Button>
          )}

          {item.isDownloadable && (
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={(event) => {
                event.stopPropagation();
                downloadOriginalImage(item);
              }}
              style={{ width: "47%" }}
            >
              <CloudDownloadIcon className="mr-2" />
              Download
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
